/* ============================================================================
   <rtb-carousel> — horizontal scroll-snap track for the product / logo /
   award rows.

   Load it with `defer` (it is a classic script, not a module):
       <script src="{{ 'rtb-carousel.js' | asset_url }}" defer></script>

   WHY A CUSTOM ELEMENT AND NOT DOMContentLoaded
   The theme editor re-renders a single section's HTML in place when a setting
   changes. Anything wired up on DOMContentLoaded is gone after the first edit,
   and anything re-wired on shopify:section:load stacks a second listener on
   every edit (the documented "carousel advances N slides per click" bug).
   connectedCallback / disconnectedCallback give us setup and teardown for free
   and are immune to the whole class of bugs.

   NO SCROLL-JACKING. The element is a plain overflow-x scroller: wheel, touch
   and trackpad are entirely native, and no scroll event is intercepted. Arrow
   keys are handled only when the scroller itself has focus, so a link inside a
   slide keeps its normal arrow-key behaviour.

   EXPECTED MARKUP
     <rtb-carousel class="rtb-carousel" aria-label="Best sellers">
       <ul class="rtb-carousel__track" data-rtb-carousel-track role="list">
         <li {{ block.shopify_attributes }}>…</li>
       </ul>

   Track children are sized and snapped by `.rtb-carousel__track > *`, so a
   slide needs no class of its own — give it one from the section's own
   {% stylesheet %} if it needs section-specific width.
     </rtb-carousel>

   Every slide's <a> exists in the DOM at load. Never virtualise slides — that
   is what turns a carousel into an SEO hole.
   ============================================================================ */

(function () {
  'use strict';

  if (customElements.get('rtb-carousel')) return;

  var SCROLL_STEP_FALLBACK = 320;

  class RtbCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rtb-carousel-track]');
      if (!this.track) return;

      // The scroller is a keyboard-operable region while it overflows. Slides
      // contain links, so this is an extra affordance, not the only one, and a
      // row that fits gets no tab stop (see _syncOverflow).
      this._ownsTabindex = !this.hasAttribute('tabindex');
      if (this._ownsTabindex) this.setAttribute('tabindex', '0');
      if (!this.hasAttribute('role')) this.setAttribute('role', 'group');

      this._onKeydown = this._handleKeydown.bind(this);
      this._onBlockSelect = this._handleBlockSelect.bind(this);

      this.addEventListener('keydown', this._onKeydown);
      document.addEventListener('shopify:block:select', this._onBlockSelect);

      this._watchOverflow();
    }

    disconnectedCallback() {
      if (this._onKeydown) this.removeEventListener('keydown', this._onKeydown);
      if (this._onBlockSelect) document.removeEventListener('shopify:block:select', this._onBlockSelect);
      if (this._resizeObserver) this._resizeObserver.disconnect();
      this._resizeObserver = null;
      this._onKeydown = null;
      this._onBlockSelect = null;
      this.track = null;
    }

    /* ----------------------------------------------------------------------
       OVERFLOW FLAG

       `data-rtb-overflow` is set while the track is actually wider than the
       scroller. Sections use it to hide controls — dot strips above all —
       that would otherwise sit under a row with nothing to scroll to.

       It is a measurement, not a breakpoint: card widths are clamps and the
       column count falls out of them, so the width at which a given row stops
       overflowing depends on how many products the merchant put in it. A
       media query would be a guess; scrollWidth is the answer.

       The attribute is ABSENT until this runs, so the CSS default must be the
       no-affordance state — a control that appears once measured is correct,
       one that appears and then vanishes is a flash of broken UI.
       ---------------------------------------------------------------------- */
    _watchOverflow() {
      this._syncOverflow();

      if (typeof ResizeObserver !== 'function') return;

      this._resizeObserver = new ResizeObserver(this._syncOverflow.bind(this));
      // The scroller for viewport changes, the track for content ones (a
      // section re-render in the theme editor, or images settling).
      this._resizeObserver.observe(this);
      this._resizeObserver.observe(this.track);
    }

    _syncOverflow() {
      var overflows = this._slidesOverflow();
      this.toggleAttribute('data-rtb-overflow', overflows);
      if (!this._ownsTabindex) return;
      if (overflows) {
        this.setAttribute('tabindex', '0');
      } else {
        this.removeAttribute('tabindex');
      }
    }

    /**
     * The SLIDES, not the scrollWidth. A track carries padding of its own —
     * the mobile peek inset, and the run-out that keeps the last card off the
     * window edge — and that padding is scrollable width without being
     * anything the shopper can scroll TO. Measured off the first and last
     * slide, a row whose cards all fit reads as "fits" however the track is
     * padded. 1px of slack for sub-pixel widths.
     */
    _slidesOverflow() {
      var slides = this.track ? this.track.children : null;
      if (!slides || slides.length === 0) return false;

      var first = slides[0].getBoundingClientRect();
      var last = slides[slides.length - 1].getBoundingClientRect();

      return last.right - first.left - this.clientWidth > 1;
    }

    /** Width of one slide plus the gap, so one key press advances one card. */
    get step() {
      var slide = this.track && this.track.firstElementChild;
      if (!slide) return SCROLL_STEP_FALLBACK;

      var gap = parseFloat(getComputedStyle(this.track).columnGap);
      if (isNaN(gap)) gap = 0;

      return slide.getBoundingClientRect().width + gap;
    }

    _handleKeydown(event) {
      // Only when the scroller itself is focused. If focus is inside a slide,
      // arrows must keep doing whatever the browser normally does.
      if (event.target !== this) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      var direction = event.key === 'ArrowRight' ? 1 : -1;
      if (getComputedStyle(this).direction === 'rtl') direction *= -1;

      event.preventDefault();
      this.scrollBy({ left: this.step * direction, behavior: this._scrollBehavior() });
    }

    /**
     * The theme editor fires this on the block's own element. Bring the slide
     * the merchant is editing into view — otherwise they report the editor
     * "not showing my change" when the slide is simply off-screen.
     */
    _handleBlockSelect(event) {
      var target = event.target;
      if (!(target instanceof Element) || !this.contains(target)) return;

      var slide = target.closest('[data-rtb-carousel-track] > *') || target;
      if (typeof slide.scrollIntoView !== 'function') return;

      slide.scrollIntoView({ behavior: this._scrollBehavior(), block: 'nearest', inline: 'center' });
    }

    _scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }
  }

  customElements.define('rtb-carousel', RtbCarousel);
})();
